import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useReviewDetail, useReviewReviewee, useReviewSubmit, useReviewSubmitModify } from "../../../api/review/hooks";
import ReviewForm from "../../organisms/ReviewForm";
import Button from "../../atoms/Button";
import RevieweeList from "../../organisms/RevieweeList";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { closeLoader, openLoader } from "../../../store/loaderSlice";
import { showAlert } from "../../../reducer/alertSlice";
import { showToast } from "../../../store/toastSlice";

/**
 * 참여할 리뷰 상세
 * - 일반 사용자용 화면
 */
const ReviewDetail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const scrollRef = useRef(null);
    const reviewIdx = useLocation().pathname.split("/").pop();

    const [reviewDetailData, setReviewDetailData] = useState();
    const [reviewAnswerDefaultData, setReviewAnswerDefaultData] = useState([]);
    const [reviewAnswerData, setReviewAnswerData] = useState([
        {
            question_idx: "",
            option_idx: null,
            answer_text: "",
        },
    ]);

    const [activeRevieweeIdx, setActiveRevieweeIdx] = useState();
    const { data: reviewData, isLoading: isReviewDetailLoading, isError: isReviewDataError, error: reviewDataError, refetch } = useReviewDetail(reviewIdx, activeRevieweeIdx)
    const { data: revieweeList, isLoading: isReviewRevieweeLoading, refetch: revieweeListRefetch } = useReviewReviewee(reviewIdx);

    const { mutate: reviewSubmitMutate, isSuccess: reviewSubmitIsSuccess, error: reviewSubmitError } = useReviewSubmit();
    const { mutate: reviewSubmitModifyMutate, isSuccess: reviewSubmitModifyIsSuccess, error: reviewSubmitModifyError } = useReviewSubmitModify();

    useEffect(() => {
        if (reviewData?.data?.review_detail) {
            setReviewDetailData(reviewData.data.review_detail);

            let obj = [];

            reviewData.data.review_detail.question.forEach((el) => {
                obj.push({
                    question_idx: el.idx,
                    option_idx: el.selected_option_idx ?? null,
                    answer_text: el.answer_text ?? "",
                });
            });

            setReviewAnswerData(obj);
            setReviewAnswerDefaultData(JSON.parse(JSON.stringify(obj)));
        }
    }, [reviewData]);

    useEffect(() => {
        isReviewDetailLoading || isReviewRevieweeLoading ? dispatch(openLoader()) : dispatch(closeLoader());
    }, [dispatch, isReviewDetailLoading, isReviewRevieweeLoading]);

    useEffect(() => {
        if (isReviewDataError && reviewDataError.response?.data?.code === -4) {
            dispatch(showAlert({
                msg: "잘못된 접근입니다.",
                func: () => {
                    navigate(-1)
                }
            }));
        }
    }, [dispatch, isReviewDataError])

    // 활성화된 Reviewee 목록 중 첫 번째 아이템이 기본 선택 되도록 처리
    useEffect(() => {
        if (activeRevieweeIdx != null) return;
        const firstActiveItem = revieweeList?.data?.find(item => item.is_active === "1")
        if (firstActiveItem) {
            setActiveRevieweeIdx(firstActiveItem.user.reviewee_idx)
        }
    }, [revieweeList, setActiveRevieweeIdx]);

    const submitHandler = (e) => {
        // 답변 수정
        if (reviewDetailData?.is_submited === "1") {
            reviewSubmitModifyMutate({
                reviewIdx: reviewIdx,
                revieweeIdx: activeRevieweeIdx,
                data: reviewAnswerData,
            },
            {
                onSuccess: () => {
                    setReviewAnswerDefaultData(JSON.parse(JSON.stringify(reviewAnswerData)));
                    dispatch(showToast({
                        msg: "답변을 수정했습니다",
                        type: "deep"
                    }))
                },
                onError: err => {
                    if (err.response?.data?.code === -2) {
                        dispatch(showAlert({
                            msg: "모든 문항에 응답해주세요."
                        }));
                    }
				}
            });
        } 
        // 답변 제출
        else {
            reviewSubmitMutate(
                {
                    reviewIdx: reviewIdx,
                    revieweeIdx: activeRevieweeIdx,
                    data: reviewAnswerData,
                },
                {
                    onSuccess: () => {
                        setReviewAnswerDefaultData(JSON.parse(JSON.stringify(reviewAnswerData)));
                        handleNextOrFinish();
                        dispatch(showToast({
                            msg: "답변을 제출했습니다",
                            type: "deep"
                        }))
                    },
                    onError: err => {
                        if (err.response?.data?.code === -2) {
                            dispatch(showAlert({
                                msg: "모든 문항에 응답해주세요."
                            }));
                        }
                    }
                }
            );
        }
    };

    // 모든 리뷰 제출을 완료했을 때만 목록 화면으로 이동 / 아직 리뷰 대상이 남았을 경우 다음 리뷰 대상으로 포커스 이동
    const handleNextOrFinish = useCallback(async () => {
        // 현재 아이템을 제출 후 새로고침이 필요하므로 리뷰이 목록을 리패치 후 사용
        const result = await revieweeListRefetch();
        const newRevieweeList = result.data?.data || [];

        // 활성화된 리뷰이만 추출 후 아직 폼을 제출하지 않은 리뷰이만 찾기
        const activeItems = newRevieweeList.filter((item) => item.is_active === "1");
        const remainItems = activeItems.filter(item => item.is_completed === "0");

        // 모두 제출 완료 했으면 목록 페이지로 이동
        if (remainItems.length === 0) {
            navigate("/review/list");
            return;
        }

        // 남은 리뷰이 중 바로 다음 아이템으로 포커스 이동
        const nextItem = remainItems[0];
        setActiveRevieweeIdx(nextItem.user.reviewee_idx);
    }, [activeRevieweeIdx, navigate, revieweeListRefetch]);

    // activeRevieweeIdx 가 바뀌면 리뷰폼을 최상단으로 스크롤
    useEffect(() => {
        const node = scrollRef.current
        if (!node) return

        requestAnimationFrame(() => {
            const scrollContainer = getScrollParent(node)
                if (scrollContainer === window) {
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
            } else {
                scrollContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" })
            }
        })
    }, [activeRevieweeIdx])

    useEffect(() => {
        console.log("@@revieweeList", revieweeList?.data);
    }, [revieweeList]);

    // 스크롤 가능한 조상 뷰를 찾아 주는 헬퍼
    function getScrollParent(el) {
        if (!el) return window
        const style = window.getComputedStyle(el)
        const overflowY = style.overflowY
        if (overflowY === "auto" || overflowY === "scroll") return el
        return getScrollParent(el.parentElement)
    }

    return (
        <>
            {reviewDetailData && (
                <>
                    <Wrap>
                        <RevieweeList 
                            idx={reviewIdx} 
                            revieweeList={revieweeList} 
                            activeRevieweeIdx={activeRevieweeIdx} 
                            setActiveReviewee={setActiveRevieweeIdx} 
                            reviewAnswerData={reviewAnswerData} 
                            setReviewAnswerData={setReviewAnswerData} 
                            reviewAnswerDefaultData={reviewAnswerDefaultData} 
                            revieweeListRefetch={() => revieweeListRefetch()}
                            isRevieweeSelectable={reviewDetailData?.is_reviewee_selectable}
                            >
                        </RevieweeList>

                        <div className="review-form" ref={scrollRef}>
                            <ReviewForm 
                                reviewDetailData={reviewDetailData} 
                                stateValue={[reviewAnswerData, setReviewAnswerData]} />
                            <Button 
                                type={reviewDetailData?.is_submited === "1" ? "main" : "deep"}
                                onClick={(e) => submitHandler(e)} 
                                style={{ width: "100%", height: "3em", marginTop: "20px" }}>
                                {reviewDetailData?.is_submited === "1" ? "답변 수정" : "답변 제출"}
                            </Button>
                        </div>
                    </Wrap>
                </>
            )}
        </>
    );
};

const Wrap = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 50px;

    .review-form {
        display: flex;
        flex-direction: column;
        width: 800px;
    }
`;

export default ReviewDetail;